// ── ContactPanel ───────────────────────────────────────────────────
//
// Right pane. MVP sections (Phase 8a):
//   1. Contact info — avatar, name, phone, account
//   2. Linked lead — link/unlink with a small search modal
//   3. Conversation settings — assign agent, status
//   4. Internal notes — list + inline add
//   5. Other channels — email/SMS placeholders (Phase 8b: wire to existing flows)
//
// Deferred to Phase 8b: tags, sentiment, activity timeline, @mention.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Link2,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Search,
  Trash2,
} from 'lucide-react';
import { Avatar } from './Avatar';
import {
  addInternalNote,
  assignConversation,
  linkLead,
  listAgents,
  searchLeads,
  unassignConversation,
  unlinkLead,
  updateConversationStatus,
  type ConversationDetailResponse,
  type ConversationStatus,
  type InternalNote,
  type LeadSearchResult,
  type UserOption,
} from '../api/whatsapp.api';
import { formatMessageTime } from '../utils/time-format';
import { formatLeadPhone } from '@/components/ui/PhoneInput';
import { SelectField } from '@/components/ui/SelectField';

const SECTIONS = ['contact', 'lead', 'settings', 'notes', 'channels'] as const;
type SectionKey = (typeof SECTIONS)[number];

const SECTION_TITLES: Record<SectionKey, string> = {
  contact: 'Contact',
  lead: 'Linked Lead',
  settings: 'Conversation',
  notes: 'Internal Notes',
  channels: 'Other Channels',
};

export function ContactPanel({
  conversation,
  onChanged,
}: {
  conversation: ConversationDetailResponse;
  onChanged: () => void;
}) {
  // Persist per-section collapse state — small luxury for power users.
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(() => {
    try {
      const raw = localStorage.getItem('wa.inbox.contactPanel.open');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return new Set(parsed.filter((s): s is SectionKey => SECTIONS.includes(s)));
      }
    } catch {
      /* ignore */
    }
    // Default: contact + lead + settings open, notes/channels closed.
    return new Set<SectionKey>(['contact', 'lead', 'settings']);
  });
  const toggle = (k: SectionKey) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      try {
        localStorage.setItem('wa.inbox.contactPanel.open', JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="flex-1 overflow-y-auto">
        {SECTIONS.map((key) => (
          <Section
            key={key}
            title={SECTION_TITLES[key]}
            open={openSections.has(key)}
            onToggle={() => toggle(key)}
          >
            {key === 'contact' && <ContactInfo conversation={conversation} />}
            {key === 'lead' && <LinkedLead conversation={conversation} onChanged={onChanged} />}
            {key === 'settings' && <Settings conversation={conversation} onChanged={onChanged} />}
            {key === 'notes' && <Notes conversation={conversation} onChanged={onChanged} />}
            {key === 'channels' && <OtherChannels conversation={conversation} />}
          </Section>
        ))}
      </div>
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────────────

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50"
        type="button"
        aria-expanded={open}
      >
        <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

// ── Sections ───────────────────────────────────────────────────────

function ContactInfo({ conversation }: { conversation: ConversationDetailResponse }) {
  const displayName = conversation.contactName || `+${conversation.contactWaId}`;
  const phoneE164 = `+${conversation.contactWaId}`;
  return (
    <div className="flex flex-col items-center text-center">
      <Avatar name={displayName} size={64} />
      <div className="mt-2 text-[15px] font-semibold text-slate-800">{displayName}</div>
      <a
        href={`tel:${phoneE164}`}
        className="mt-0.5 text-[12px] text-indigo-600 hover:underline"
      >
        {phoneE164}
      </a>
      {conversation.account?.verifiedName && (
        <div className="mt-1 text-[11px] text-slate-500">
          via <span className="font-medium">{conversation.account.verifiedName}</span>
        </div>
      )}
    </div>
  );
}

function LinkedLead({
  conversation,
  onChanged,
}: {
  conversation: ConversationDetailResponse;
  onChanged: () => void;
}) {
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<LeadSearchResult[]>([]);

  useEffect(() => {
    if (!searching) return;
    const t = window.setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      try {
        const res = await searchLeads(q.trim());
        setResults(res);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [q, searching]);

  const doLink = async (leadId: string) => {
    setBusy(true);
    try {
      await linkLead(conversation.id, leadId);
      setSearching(false);
      setQ('');
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  const doUnlink = async () => {
    setBusy(true);
    try {
      await unlinkLead(conversation.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (conversation.lead) {
    return (
      <div className="rounded-md border border-gray-200 bg-slate-50 px-3 py-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="truncate text-[13px] font-medium text-slate-800">
              {conversation.lead.name}
            </div>
            <div className="text-[11px] text-slate-500">
              Status: <span className="font-medium">{conversation.lead.status}</span>
            </div>
            <a
              href={`/leads/${conversation.lead.id}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:underline"
            >
              <Link2 className="h-3 w-3" /> Open lead
            </a>
          </div>
          <button
            onClick={doUnlink}
            disabled={busy}
            className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-red-500 disabled:opacity-50"
            title="Unlink"
            aria-label="Unlink lead"
            type="button"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (searching) {
    return (
      <div className="space-y-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search leads by name or phone"
            className="w-full rounded-md border border-gray-200 pl-8 pr-2.5 py-1.5 text-sm focus:border-indigo-300 focus:outline-none"
            autoFocus
          />
        </div>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {results.length === 0 && q.trim().length >= 2 && (
            <div className="text-center text-[11px] italic text-slate-400">No matches</div>
          )}
          {results.map((l) => (
            <button
              key={l.id}
              onClick={() => doLink(l.id)}
              disabled={busy}
              className="w-full rounded-md px-2 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
              type="button"
            >
              <div className="truncate text-[12.5px] font-medium text-slate-800">{l.name}</div>
              <div className="text-[10.5px] text-slate-500">
                {l.status}
                {formatLeadPhone(l.phones?.[0]) && ` · ${formatLeadPhone(l.phones?.[0])}`}
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={() => {
            setSearching(false);
            setQ('');
          }}
          className="text-[11px] text-slate-500 hover:underline"
          type="button"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setSearching(true)}
      className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-[12px] text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
      type="button"
    >
      <Link2 className="mr-1 inline h-3.5 w-3.5" />
      Link to existing lead
    </button>
  );
}

function Settings({
  conversation,
  onChanged,
}: {
  conversation: ConversationDetailResponse;
  onChanged: () => void;
}) {
  const [agents, setAgents] = useState<UserOption[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listAgents()
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  const onAssign = async (id: string) => {
    if (busy) return;
    setBusy(true);
    try {
      if (id === '__unassign__') {
        await unassignConversation(conversation.id);
      } else {
        await assignConversation(conversation.id, id);
      }
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onStatus = async (s: ConversationStatus) => {
    if (busy) return;
    setBusy(true);
    try {
      await updateConversationStatus(conversation.id, s);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      <Row label="Assigned to">
        <SelectField
          value={conversation.assignedAgentId ?? '__unassign__'}
          onChange={(e) => onAssign(e.target.value)}
          disabled={busy}
          className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[12.5px] focus:border-indigo-300 focus:outline-none disabled:opacity-50"
        >
          <option value="__unassign__">— Unassigned —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </SelectField>
      </Row>
      <Row label="Status">
        <SelectField
          value={conversation.status}
          onChange={(e) => onStatus(e.target.value as ConversationStatus)}
          disabled={busy || conversation.status === 'ARCHIVED'}
          className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[12.5px] focus:border-indigo-300 focus:outline-none disabled:opacity-50"
        >
          <option value="OPEN">Open</option>
          <option value="SNOOZED">Snoozed</option>
          <option value="CLOSED">Closed</option>
          <option value="ARCHIVED">Archived (terminal)</option>
        </SelectField>
      </Row>
      {busy && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </div>
      )}
    </div>
  );
}

function Notes({
  conversation,
  onChanged,
}: {
  conversation: ConversationDetailResponse;
  onChanged: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const notes: InternalNote[] = useMemo(
    () =>
      [...conversation.internalNotes].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [conversation.internalNotes],
  );

  const add = useCallback(async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await addInternalNote(conversation.id, body);
      setText('');
      onChanged();
    } finally {
      setBusy(false);
    }
  }, [text, busy, conversation.id, onChanged]);

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an internal note…"
          className="w-full rounded-md border border-amber-200 bg-amber-50/30 px-2.5 py-1.5 text-[13px] focus:border-amber-300 focus:outline-none"
          rows={2}
        />
        <button
          onClick={add}
          disabled={!text.trim() || busy}
          className="w-full rounded-md bg-amber-500 px-2 py-1 text-[12px] font-medium text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-slate-300"
          type="button"
        >
          {busy ? 'Saving…' : 'Add note'}
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="text-center text-[11px] italic text-slate-400">No notes yet</div>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5">
              <div className="flex items-center justify-between text-[10.5px] font-medium text-amber-700">
                <span>{n.author?.name ?? n.authorName}</span>
                <span>{formatMessageTime(n.createdAt)}</span>
              </div>
              <div className="mt-0.5 whitespace-pre-wrap text-[12.5px] text-amber-900">
                {n.body}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OtherChannels({ conversation }: { conversation: ConversationDetailResponse }) {
  const phoneE164 = `+${conversation.contactWaId}`;
  // Email is available only if a lead with an email is linked. The lead
  // sub-object on the conversation summary doesn't carry email, so link
  // to the lead detail page instead of trying to pre-fetch.
  const leadId = conversation.lead?.id ?? null;
  return (
    <div className="grid grid-cols-3 gap-2">
      {leadId ? (
        <a
          href={`/leads/${leadId}`}
          className="flex flex-col items-center gap-1 rounded-md border border-gray-200 px-2 py-2 text-[11px] text-slate-600 hover:bg-slate-50"
          title="Open lead to send email"
        >
          <Mail className="h-4 w-4" />
          Email
        </a>
      ) : (
        <div
          className="flex flex-col items-center gap-1 rounded-md border border-gray-200 px-2 py-2 text-[11px] text-slate-400 cursor-not-allowed opacity-60"
          title="Link a lead to enable email"
        >
          <Mail className="h-4 w-4" />
          Email
        </div>
      )}
      <a
        href={`tel:${phoneE164}`}
        className="flex flex-col items-center gap-1 rounded-md border border-gray-200 px-2 py-2 text-[11px] text-slate-600 hover:bg-slate-50"
        title={`Call ${phoneE164}`}
      >
        <Phone className="h-4 w-4" />
        Call
      </a>
      <div
        className="flex flex-col items-center gap-1 rounded-md border border-gray-200 px-2 py-2 text-[11px] text-slate-400 cursor-not-allowed opacity-60"
        title="SMS coming soon"
      >
        <MessageSquare className="h-4 w-4" />
        SMS
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 flex-shrink-0 text-[11px] font-medium text-slate-500">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

